import type { FC } from "react";
import { useMemo } from "react";

import { isEditing } from "@builder.io/sdk-react";

import type { DeviceAllowedCodeLength } from "@jti/fe-common";
import { SmartSurveyPushBlock as UISmartSurveyPushBlock } from "@jti/ui/components/SmartSurveyPushBlock";

import type { smartSurveyPushBlockInputs } from "./SmartSurveyPushBlock.builder";

type SimplifiedPrizesBuilderProps = BuilderComponentProps<typeof smartSurveyPushBlockInputs>;

const getRequiredCodeLength = (
  is12DeviceCodeLength?: boolean,
  is15DeviceCodeLength?: boolean,
  is16DeviceCodeLength?: boolean
) => {
  const requiredCodeLength: DeviceAllowedCodeLength[] = [];
  if (is12DeviceCodeLength) {
    requiredCodeLength.push(12);
  }
  if (is15DeviceCodeLength) {
    requiredCodeLength.push(15);
  }
  if (is16DeviceCodeLength) {
    requiredCodeLength.push(16);
  }
  if (requiredCodeLength.length === 0) {
    requiredCodeLength.push(16);
  }
  return requiredCodeLength;
};

export const SmartSurveyPushBlock: FC<SimplifiedPrizesBuilderProps> = ({
  alt,
  type,
  title,
  ctaUrl,
  ctaText,
  subtitle,
  imgMobile,
  preCtaText,
  imgDesktop,
  buttonText,
  description,
  orientation,
  inputPlaceholder,
  surveyMonkeyPageUrl,
  dateOfUserRegistrationForSurvey,
  titleAllCompletedRecurringSurveys,
  subtitleAllCompletedRecurringSurveys,
  ctaButtonAllCompletedRecurringSurveys,
  descriptionAllCompletedRecurringSurveys,
  redirectUrlAllCompletedRecurringSurveys,
  missingDeviceRegistrationDescription,
  theme,
  is12Characters,
  is15Characters,
  is16Characters,
  registerDeviceImgAlt,
  registerDeviceImgMobile,
  registerDeviceImgDesktop,
  registerDeviceDialogTitle,
  registerDeviceAccordionTitle,
  registerDeviceDialogDescription,
  registerDeviceAccordionButtonUrl,
  registerDeviceAccordionButtonText,
  cancelDeviceRegistrationButtonText,
  registerDeviceAccordionDescription,
  confirmDeviceRegistrationButtonText,
  isDeviceCodeFormatLabelVisible,
}) => {
  const requiredCodeLength = useMemo(
    () => getRequiredCodeLength(is12Characters, is15Characters, is16Characters),
    [is12Characters, is15Characters, is16Characters]
  );
  return (
    <UISmartSurveyPushBlock
      isInEditor={isEditing()}
      alt={alt}
      type={type}
      title={title}
      ctaUrl={ctaUrl}
      ctaText={ctaText}
      subtitle={subtitle}
      imgMobile={imgMobile}
      preCtaText={preCtaText}
      imgDesktop={imgDesktop}
      buttonText={buttonText}
      description={description}
      orientation={orientation}
      inputPlaceholder={inputPlaceholder}
      surveyMonkeyPageUrl={surveyMonkeyPageUrl}
      dateOfUserRegistrationForSurvey={dateOfUserRegistrationForSurvey}
      titleAllCompletedRecurringSurveys={titleAllCompletedRecurringSurveys}
      subtitleAllCompletedRecurringSurveys={subtitleAllCompletedRecurringSurveys}
      ctaButtonAllCompletedRecurringSurveys={ctaButtonAllCompletedRecurringSurveys}
      descriptionAllCompletedRecurringSurveys={descriptionAllCompletedRecurringSurveys}
      redirectUrlAllCompletedRecurringSurveys={redirectUrlAllCompletedRecurringSurveys}
      missingDeviceRegistrationDescription={missingDeviceRegistrationDescription}
      theme={theme}
      requiredCodeLength={requiredCodeLength}
      registerDeviceImgAlt={registerDeviceImgAlt}
      registerDeviceImgMobile={registerDeviceImgMobile}
      registerDeviceImgDesktop={registerDeviceImgDesktop}
      registerDeviceDialogTitle={registerDeviceDialogTitle}
      registerDeviceAccordionTitle={registerDeviceAccordionTitle}
      registerDeviceDialogDescription={registerDeviceDialogDescription}
      registerDeviceAccordionButtonUrl={registerDeviceAccordionButtonUrl}
      registerDeviceAccordionButtonText={registerDeviceAccordionButtonText}
      cancelDeviceRegistrationButtonText={cancelDeviceRegistrationButtonText}
      registerDeviceAccordionDescription={registerDeviceAccordionDescription}
      confirmDeviceRegistrationButtonText={confirmDeviceRegistrationButtonText}
      isDeviceCodeFormatLabelVisible={isDeviceCodeFormatLabelVisible}
    />
  );
};
